import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';
import { HttpError, RQAPICall, RQAPICallWithData } from 'store/APIClient';
import { Notification } from './types';

export const useMarkSingleNotificationRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (notificationId: number) => {
            // We make the assumption that the call is going to be successful
            // and mark the notification as read before the call returns for
            // a better UX.
            queryClient.setQueryData(
                ['notifications'],
                (notifications: Notification[]) =>
                    notifications.map((n) => {
                        if (n.id === notificationId) {
                            return { ...n, read: true };
                        }
                        return n;
                    }),
            );

            await RQAPICall({
                endpoint: `notifications/${notificationId}/mark_as_read`,
                method: 'PATCH',
                body: {
                    read: true,
                },
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
        },

        onError: () => {
            // We also need to invalidate query on error because we messed with
            // the query data.
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
        },
    });
};

export const useMarkAllNotificationRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            // We make the assumption that the call is going to be successful
            // and mark the notifications as read before the call returns for
            // a better UX.
            queryClient.setQueryData(
                ['notifications'],
                (notifications: Notification[]) =>
                    notifications.map((n) => ({ ...n, read: true })),
            );

            await RQAPICall({
                endpoint: 'notifications/mark_as_read',
                method: 'PATCH',
                body: {
                    read: true,
                },
            });
        },

        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
            queryClient.invalidateQueries({
                queryKey: ['unread_notifications'],
            });
        },

        onError: () => {
            // We also need to invalidate query on error because we messed with
            // the query data.
            queryClient.invalidateQueries({
                queryKey: ['notifications'],
            });
        },
    });
};

export const useGetNotifications = (
    enabled: boolean,
): UseQueryResult<Notification[], HttpError> =>
    useQuery({
        queryKey: ['notifications'],

        queryFn: async () => {
            const response = await RQAPICallWithData<Notification[]>({
                endpoint: `notifications`,
                method: 'GET',
            });

            return response.content.data;
        },

        enabled,

        // 30sec refetch interval
        refetchInterval: 30000,
    });
